import React from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import LanguageSwitcher from '../components/LanguageSwitcher';
import PrivacyPolicyAr from './ar/PrivacyPolicyAr';

const PrivacyPolicy = () => {
  const { t, lang } = useI18n();
  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/login">
            <img
              src={`${process.env.PUBLIC_URL}/Logo-ArabCap.svg`}
              alt="ArabCap"
              className="h-8 w-auto"
            />
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher compact />
            <Link 
              to="/login" 
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
            >
              {t('Back to Login')}
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {lang === 'ar' ? <PrivacyPolicyAr /> : (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12">
          <h1 className="text-3xl font-extrabold text-slate-900 mb-2">Privacy Policy</h1>
          <p className="text-slate-500 mb-8">Last updated: January 27, 2026</p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">1. Introduction</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Welcome to ArabCap. We respect your privacy and are committed to protecting your personal data. 
                This privacy policy explains how we collect, use, and safeguard your information when you use our 
                AI-powered Arabic subtitle generation service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">2. Information We Collect</h2>
              <p className="text-slate-600 leading-relaxed mb-4">We collect the following types of information:</p>
              <ul className="list-disc ps-6 text-slate-600 space-y-2 mb-4">
                <li><strong>Account Information:</strong> Name, email address, and password when you create an account.</li>
                <li><strong>Content:</strong> Video and audio files you upload for subtitle generation.</li>
                <li><strong>Usage Data:</strong> Information about how you interact with our service, including features used and time spent.</li>
                <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-slate-600 leading-relaxed mb-4">We use your information to:</p>
              <ul className="list-disc ps-6 text-slate-600 space-y-2 mb-4">
                <li>Provide and improve our subtitle generation services</li>
                <li>Process your uploaded content and generate accurate subtitles</li>
                <li>Communicate with you about your account and service updates</li>
                <li>Ensure the security and integrity of our platform</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">4. Data Storage and Security</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Your data is stored securely using industry-standard encryption. We implement appropriate technical 
                and organizational measures to protect your personal data against unauthorized access, alteration, 
                disclosure, or destruction.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                Uploaded video and audio files are processed for subtitle generation and may be temporarily stored 
                to provide our services. You can delete your content at any time through your dashboard.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">5. Data Sharing</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                We do not sell your personal data. We may share your information with:
              </p>
              <ul className="list-disc ps-6 text-slate-600 space-y-2 mb-4">
                <li><strong>Service Providers:</strong> Third-party services that help us operate our platform (hosting, analytics, payment processing).</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">6. Your Rights</h2>
              <p className="text-slate-600 leading-relaxed mb-4">You have the right to:</p>
              <ul className="list-disc ps-6 text-slate-600 space-y-2 mb-4">
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Delete your account and associated data</li>
                <li>Export your data</li>
                <li>Opt out of marketing communications</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">7. Cookies</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                We use essential cookies to keep you logged in and provide our services. We may also use analytics 
                cookies to understand how you use our platform and improve our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">8. Children's Privacy</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Our service is not intended for children under 13 years of age. We do not knowingly collect 
                personal information from children under 13.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">9. Changes to This Policy</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                We may update this privacy policy from time to time. We will notify you of any significant changes 
                by posting the new policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">10. Contact Us</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                If you have any questions about this privacy policy or our data practices, please contact us at:
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong>Email:</strong> privacy@arabcap.co
              </p>
            </section>
          </div>
        </div>
        )}

        {/* Footer Links */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <Link to="/terms" className="hover:text-slate-700 underline">{t('Terms of Service')}</Link>
          <span className="mx-2">·</span>
          <Link to="/login" className="hover:text-slate-700 underline">{t('Back to Login')}</Link>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
